/**
 * The meta tree screen (TRD-09 §10) — a radial constellation of ~1000 nodes.
 *
 * Layout comes entirely from `UnlockNode.position` (R-09-22): the data authors a circle
 * and this projects it. At this scale nodes are compact dots in three visual tiers —
 * one-Shard ladder steps small, trunk/technique nodes medium, keystones and the
 * synthesis seams large — and everything a node MEANS lives in the info panel that
 * follows the hover.
 *
 * Hovering any unowned node keeps its whole prerequisite chain lit and dims the rest
 * (R-09-24) — at a thousand nodes that hover is the difference between a constellation
 * and a wall. Every mutation goes through the pure functions in `meta.ts`.
 *
 * Perf shape: the node/edge world is memoized on (save, hover) — cursor tracking and
 * pan/zoom only move a transform, they never rebuild a thousand DOM nodes.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { CLASS_COLORS, UNLOCK_TREE, getClass, type UnlockNode } from '@prism/content';
import type { ClassId } from '@prism/sim';
import type { SaveData } from '../persistence/save.js';
import {
  buyNode,
  nodeStates,
  prerequisiteChain,
  respec,
  shardsAvailable,
  type NodeState,
} from '../meta/meta.js';

interface MetaTreeProps {
  save: SaveData;
  onSave: (next: SaveData) => void;
  onClose: () => void;
}

/** Authored 0–100 coordinates → pixels at zoom 1. The wheel is a 2400px world. */
const UNIT = 24;

/** The class whose spoke a node sits on, for tinting — from payload or id prefix. */
function classOf(node: UnlockNode): ClassId | null {
  const p = node.payload;
  if (p.k === 'class') return p.classId;
  if (p.k === 'effect' && p.forClass) return p.forClass;
  const prefix = node.id.split('-')[0];
  return prefix && prefix in CLASS_COLORS ? (prefix as ClassId) : null;
}

/** Synthesis seams read as their own species regardless of which class they scope to. */
const isSynthesis = (node: UnlockNode): boolean =>
  node.id.startsWith('syn-') || node.id.startsWith('apex-');

function glyphOf(node: UnlockNode): string {
  if (node.id.startsWith('apex-')) return '✦';
  if (node.id.startsWith('syn-')) return '✧';
  const cls = classOf(node);
  if (cls) return getClass(cls).glyph;
  if (node.payload.k === 'map') return '◆';
  if (node.payload.k === 'start') return '▲';
  return '●';
}

/** Three visual tiers: ladder smalls, trunk/techniques, keystones + synthesis. */
function dotSize(node: UnlockNode): number {
  if (node.id.startsWith('apex-')) return 40;
  if (node.id.endsWith('keystone') || node.id.startsWith('syn-')) return 36;
  if (node.id.endsWith('-peak')) return 30;
  return node.cost === 1 ? 16 : 28;
}

const STATE_LABEL: Record<NodeState, string> = {
  owned: 'Owned',
  affordable: 'Click to buy',
  locked: 'Locked — needs its prerequisites',
  unaffordable: 'Not enough Shards',
};

export function MetaTree({ save, onSave, onClose }: MetaTreeProps) {
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.32);
  const [hovered, setHovered] = useState<string | null>(null);
  const [confirmRespec, setConfirmRespec] = useState(false);
  const dragging = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(
    null,
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest('.meta-node')) return;
      dragging.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [pan],
  );

  /** Cursor position within the canvas, so the info panel can follow the mouse. */
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    const d = dragging.current;
    if (!d) return;
    setPan({ x: d.panX + (e.clientX - d.startX), y: d.panY + (e.clientY - d.startY) });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = null;
  }, []);

  const onWheel = useCallback((e: React.WheelEvent) => {
    setZoom((z) => Math.min(2.5, Math.max(0.12, z * (e.deltaY > 0 ? 0.9 : 1.1))));
  }, []);

  const byId = useMemo(() => new Map(UNLOCK_TREE.map((n) => [n.id, n])), []);
  const hoveredNode = hovered ? byId.get(hovered) : undefined;

  // One pass for every node's state — per-node nodeState() is quadratic at this scale.
  const states = useMemo(() => nodeStates(save), [save]);

  // Chain-light on hover for ANY unowned node — "how do I get there" is the question
  // for affordable and locked alike, so only owned hovers skip the dim.
  const chain = useMemo(
    () =>
      hoveredNode && states.get(hoveredNode.id) !== 'owned'
        ? prerequisiteChain(hoveredNode.id)
        : null,
    [hoveredNode, states],
  );

  const available = shardsAvailable(save);

  // The thousand-node world rebuilds only when ownership or the hover chain changes —
  // NOT on cursor moves or pan/zoom, which stay pure-transform.
  const world = useMemo(
    () => (
      <>
        <svg className="meta-edges" width={100 * UNIT} height={100 * UNIT}>
          {UNLOCK_TREE.flatMap((node) =>
            node.requires.map((req) => {
              const from = byId.get(req);
              if (!from) return null;
              const inChain = chain ? chain.has(node.id) && chain.has(req) : true;
              return (
                <line
                  key={`${req}->${node.id}`}
                  x1={from.position.x * UNIT}
                  y1={from.position.y * UNIT}
                  x2={node.position.x * UNIT}
                  y2={node.position.y * UNIT}
                  className={inChain ? 'meta-edge' : 'meta-edge meta-edge--dim'}
                />
              );
            }),
          )}
        </svg>

        {UNLOCK_TREE.map((node) => {
          const state = states.get(node.id) ?? 'locked';
          const dimmed = chain !== null && !chain.has(node.id);
          const cls = classOf(node);
          const accent = cls && !isSynthesis(node) ? CLASS_COLORS[cls].color : '#e8c95a';
          const size = dotSize(node);
          return (
            <button
              key={node.id}
              type="button"
              className={`meta-node meta-node--${state}${dimmed ? ' meta-node--dimmed' : ''}${
                size <= 16 ? ' meta-node--small' : ''
              }`}
              style={{
                left: node.position.x * UNIT - size / 2,
                top: node.position.y * UNIT - size / 2,
                width: size,
                height: size,
                color: accent,
                ...(state === 'owned' || state === 'affordable' ? { borderColor: accent } : {}),
              }}
              onPointerEnter={() => setHovered(node.id)}
              onPointerLeave={() => setHovered(null)}
              onClick={() => {
                const next = buyNode(save, node.id);
                if (next) onSave(next);
              }}
            >
              {glyphOf(node)}
            </button>
          );
        })}
      </>
    ),
    [states, chain, byId, save, onSave],
  );

  return (
    <div className="meta-overlay">
      <header className="meta-topbar">
        <span className="brand">UNLOCK TREE</span>
        <span className="meta-shards">✦ {available} Shards</span>
        <span className="muted">
          earned {save.shardsEarned} · spent {save.shardsSpent} · {save.ownedNodes.length}/
          {UNLOCK_TREE.length} owned
        </span>
        <span className="spacer" />
        {confirmRespec ? (
          <>
            <span className="muted">Refund all {save.shardsSpent} spent Shards?</span>
            <button
              type="button"
              className="primary"
              onClick={() => {
                onSave(respec(save));
                setConfirmRespec(false);
              }}
            >
              Yes, respec
            </button>
            <button type="button" onClick={() => setConfirmRespec(false)}>
              Keep
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={save.shardsSpent === 0}
            title={save.shardsSpent === 0 ? 'Nothing spent yet' : 'Refund every spent Shard'}
            onClick={() => setConfirmRespec(true)}
          >
            Respec
          </button>
        )}
        <button type="button" onClick={onClose}>
          ✕ Close
        </button>
      </header>

      <div
        className="meta-canvas"
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
      >
        <div
          className="meta-world meta-world--centered"
          style={{
            transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`,
          }}
        >
          {world}
        </div>

        {/* The hover panel — what this upgrade DOES, riding beside the cursor so the
            eye never has to leave the node it is asking about. Flips to the other side
            of the pointer near the right/bottom edges instead of clipping. */}
        {hoveredNode && (
          <aside
            className="meta-info"
            style={(() => {
              const rect = canvasRef.current?.getBoundingClientRect();
              const w = 300;
              const h = 130;
              const pad = 18;
              const flipX = rect ? cursor.x + pad + w > rect.width : false;
              const flipY = rect ? cursor.y + pad + h > rect.height : false;
              return {
                left: flipX ? cursor.x - pad - w : cursor.x + pad,
                top: flipY ? cursor.y - pad - h : cursor.y + pad,
              };
            })()}
          >
            <header>
              <b
                style={{
                  color: (() => {
                    const cls = classOf(hoveredNode);
                    return cls ? CLASS_COLORS[cls].color : '#e8c95a';
                  })(),
                }}
              >
                {hoveredNode.name}
              </b>
              <span className="meta-shards">✦ {hoveredNode.cost}</span>
            </header>
            <p>{hoveredNode.summary}</p>
            <p className="muted">{STATE_LABEL[states.get(hoveredNode.id) ?? 'locked']}</p>
            {states.get(hoveredNode.id) === 'locked' && (
              /* Per-requirement ticks matter once nodes can gate on SEVERAL branches
                 (the synthesis seams): show which half of the pact is still missing. */
              <p className="muted">
                Requires:{' '}
                {hoveredNode.requires.map((r, i) => (
                  <span key={r}>
                    {i > 0 && ', '}
                    <span className={states.get(r) === 'owned' ? 'meta-req--met' : 'meta-req--missing'}>
                      {states.get(r) === 'owned' ? '✓' : '✗'} {byId.get(r)?.name ?? r}
                    </span>
                  </span>
                ))}
              </p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
